import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CheckCircle2, MessageCircle, Copy, Mail, ExternalLink, Phone } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Post-creation dialog that shows the newly created client's credentials
 * with explicit action buttons for sending via WhatsApp / copying / opening.
 * No auto-open — admin must click the button.
 */
const PostCreateClientDialog = ({ open, onOpenChange, client, waLink, emailSent, emailError }) => {
  if (!client) return null;

  const link = `${window.location.origin}/c/${client.slug}`;
  const password = client.__password || '';
  const loginId = client.email || client.name;
  const fullMessage = `Ciao ${client.name},\n\nLe tue foto sono pronte nella tua area riservata FRAMEBOX by Nicola Verardo.\n\nLink: ${link}\nNome: ${loginId}\nPassword: ${password}\n\nA presto,\nNicola`;

  const copyLink = () => { navigator.clipboard.writeText(link); toast.success('Link copiato'); };
  const copyPassword = () => { navigator.clipboard.writeText(password); toast.success('Password copiata'); };
  const copyMessage = () => { navigator.clipboard.writeText(fullMessage); toast.success('Messaggio completo copiato'); };
  const openWA = () => {
    if (!waLink) return toast.error('Numero WhatsApp non impostato per questo cliente');
    window.open(waLink, '_blank', 'noopener,noreferrer');
    toast.success('WhatsApp aperto — clicca Invia');
  };
  const openPreview = () => { window.open(link, '_blank', 'noopener,noreferrer'); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2 text-emerald-600 mb-1">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-widest">Cliente creato</span>
          </div>
          <DialogTitle className="font-display text-2xl">Invia credenziali a {client.name}</DialogTitle>
          <DialogDescription>Scegli come inviare al cliente il link + password. Nessun invio automatico: usa i pulsanti qui sotto.</DialogDescription>
        </DialogHeader>

        {/* Credentials preview */}
        <div className="mt-3 space-y-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Link cliente</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono text-slate-800 break-all">{link}</code>
              <button onClick={copyLink} className="shrink-0 text-slate-500 hover:text-[#B8944B] p-1" title="Copia link"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Password</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs font-mono text-slate-800">{password || <span className="italic text-slate-400">non disponibile</span>}</code>
              {password && <button onClick={copyPassword} className="shrink-0 text-slate-500 hover:text-[#B8944B] p-1" title="Copia password"><Copy className="w-4 h-4" /></button>}
            </div>
          </div>
        </div>

        {/* Email status */}
        {client.email && (
          <div className={`mt-3 rounded-xl px-3 py-2 text-sm flex items-center gap-2 ${emailSent ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
            <Mail className="w-4 h-4" />
            {emailSent
              ? <span>Email inviata a <b>{client.email}</b></span>
              : <span>Email <b>non</b> inviata a {client.email} {emailError && <span className="text-[11px]">({emailError})</span>}</span>
            }
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            data-testid="postcreate-open-wa"
            onClick={openWA}
            disabled={!waLink}
            className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${waLink ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
          >
            <MessageCircle className="w-4 h-4" />
            {waLink ? 'Invia via WhatsApp' : 'WhatsApp non disponibile'}
          </button>
          <button
            data-testid="postcreate-copy-message"
            onClick={copyMessage}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold bg-black hover:bg-[#C9A961] hover:text-black text-white transition-colors"
          >
            <Copy className="w-4 h-4" /> Copia messaggio completo
          </button>
          <button
            data-testid="postcreate-preview"
            onClick={openPreview}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium bg-white border border-slate-200 hover:border-[#C9A961] text-slate-700"
          >
            <ExternalLink className="w-4 h-4" /> Anteprima area cliente
          </button>
          <button
            data-testid="postcreate-close"
            onClick={()=>onOpenChange(false)}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Chiudi
          </button>
        </div>

        {!waLink && client.phone_wa === '' && (
          <p className="mt-2 text-[11px] text-slate-500 flex items-start gap-1.5">
            <Phone className="w-3 h-3 mt-0.5 shrink-0" />
            Suggerimento: aggiungi il numero WhatsApp del cliente nel form per abilitare l'invio diretto.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PostCreateClientDialog;
